import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { useMyOrderPage } from '../../../../hooks/useMyOrderPage';
import Checkout from './Checkout';
import PaymentButton from './components/PaymentButton';
import {
  BackgroundCheck,
  WeekOrderSummary,
  OrderDetails,
  HeaderText,
  PaymentProcessing,
} from './components';
import ErrorModal from 'core/ui/components/V2/ErrorModal';
import { COMPLETE_USER_SWAP_PURCHASE } from 'core/api/apollo/mutations';
import { Box, Hr } from 'core/ui/atoms';
import { NavigationHeader } from 'onramp/ui/components';
import Loading from 'core/ui/components/Loading';
import { confirmationPath } from 'onramp/api/navigation';

const MyOrderPage = () => {
  const history = useHistory();
  const {
    isLoading,
    deliveryDays,
    getUserSwapsForDeliveryDay,
    handleDeleteSwapRequest,
    refetchOrders,
    userSwapBillingSummary,
    refetchBillingSummary,
    finalAmount,
  } = useMyOrderPage();

  const [backgroundJobId, setBackgroundJobId] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [completeUserSwapPurchase] = useMutation(COMPLETE_USER_SWAP_PURCHASE);

  const goToConfirmationPath = () => history.push(confirmationPath(userSwapBillingSummary?.invoiceId));

  const handleBackgroundCheckSuccess = async () => {
    // Refetch billing summary to get the updated invoiceId
    const { data } = await refetchBillingSummary();
    // Use the fresh invoiceId from the refetch result
    const freshInvoiceId = data?.getUserSwapBillingSummary?.invoiceId;
    if (freshInvoiceId) {
      history.push(confirmationPath(freshInvoiceId));
    } else {
      // Fallback to the original method if no fresh invoiceId
      goToConfirmationPath();
    }
  };

  const deliveryDayIds = deliveryDays.map((day) => day.id);

  const onComplete = async () => {
    setIsProcessing(true);
    setErrorMsg(null);

    return completeUserSwapPurchase({ variables: { input: { deliveryDays: deliveryDayIds.toString() } } })
      .then(({ data }) => {
        // eslint-disable-next-line no-console
        console.log('Purchase completed:', data);
        const { completeUserSwapPurchase: { backgroundJob, errors } } = data;
        if (errors && errors.length > 0) {
          setErrorMsg(errors[0].message);
          setIsProcessing(false);
          return;
        }
        setBackgroundJobId(backgroundJob?.id);
        setPurchase(purchase);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error completing purchase:', error);
        setErrorMsg(error.message || 'An error occurred while processing your purchase.');
        setIsProcessing(false);
      });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isProcessing) {
    return (
      <>
        <PaymentProcessing />
        <BackgroundCheck
          open={isProcessing}
          backgroundJobId={backgroundJobId}
          onSuccess={handleBackgroundCheckSuccess}
          onError={() => setIsProcessing(false)}
        />
      </>
    );
  }

  return (
    <Box>
      <NavigationHeader title="My Shopping Cart" />
      <Hr />
      <HeaderText>Summary</HeaderText>
      <WeekOrderSummary
        orderData={getUserSwapsForDeliveryDay}
        deliveryDays={deliveryDays}
        onDeleteSwapRequest={handleDeleteSwapRequest}
        refetchOrders={refetchOrders}
        refetchBillingSummary={refetchBillingSummary}
      />
      <Hr />
      <Checkout userSwapsForDeliveryDay={getUserSwapsForDeliveryDay} />
      <Hr />
      <OrderDetails />
      <PaymentButton
        onComplete={onComplete}
        deliveryDayIds={deliveryDayIds}
        userSwapBillingSummary={userSwapBillingSummary}
        finalAmount={finalAmount}
      />
      {errorMsg && <ErrorModal
        title='Payment Error'
        description={errorMsg}
        ctaText='OK'
        onCtaClick={() => setErrorMsg(null)}
        onClose={() => setErrorMsg(null)}
      />}
    </Box>
  );
};

export default MyOrderPage;
